import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Input, Space, Spin, Tag, Typography, message } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Staff review of what customers sent with their approvals (spec §7).
 *
 * A customer who approves an estimate may correct their own details on the way
 * through. Nothing they typed is on `customers` / `vehicles` yet — it is parked
 * as pending change requests. This screen is where a human decides, field by
 * field, whether the customer was right, or bounces the submission back with a
 * reason the customer can act on.
 *
 * ── Everything here comes off the wire ─────────────────────────────────────
 *
 * There is no field list, no policy and no label table of record in this file.
 * `label` arrives with each change from `GET /approval-review/queue`, which gets
 * it from the same `labelForField` the server's own emails use. `humanize` below
 * is a last-resort fallback for a field the server did not label — never a second
 * copy of the policy.
 *
 * Accept defaults to ACCEPT on every change: the common case is the customer
 * being right about their own address, and a screen that defaults to rejecting
 * would train staff to click through it.
 */

export interface ApprovalChange {
  id: number;
  target_type: string;
  field_name: string;
  /** Server-supplied display label. Optional so an older payload still renders. */
  label?: string | null;
  current_value: string | null;
  requested_value: string | null;
  status: string;
}

export interface ApprovalSubmission {
  estimate_id: number;
  estimate_number: string | null;
  state: string;
  submitted_at: string | null;
  return_count: number;
  changes: ApprovalChange[];
}

export interface StalledEstimate {
  estimate_id: number;
  estimate_number: string | null;
  missing: string[];
}

export interface StalledSummary {
  count: number;
  estimates: StalledEstimate[];
}

/** Fallback only — see the note above. `vin` would otherwise read "Vin". */
const ACRONYMS: Record<string, string> = { vin: 'VIN' };

function humanize(field: string): string {
  if (ACRONYMS[field]) return ACRONYMS[field];
  const words = field.replace(/_/g, ' ').trim();
  if (!words) return field;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const labelOf = (change: ApprovalChange): string =>
  (change.label && change.label.trim()) || humanize(change.field_name);

const EMPTY = '—';

const shownValue = (value: string | null): string =>
  value === null || value === undefined || String(value).trim() === '' ? EMPTY : String(value);

/** Per-submission editing state. Keyed by estimate id. */
interface CardState {
  /** change id -> accept. Absent means accept (the default). */
  decisions: Record<number, boolean>;
  /** change id -> note the customer will read against that field. */
  fieldNotes: Record<number, string>;
  returnOpen: boolean;
  returnNote: string;
  error: string;
  busy: boolean;
}

const BLANK: CardState = {
  decisions: {},
  fieldNotes: {},
  returnOpen: false,
  returnNote: '',
  error: '',
  busy: false,
};

/**
 * Which role's estimate route to link to.
 *
 * Read off the URL rather than from auth state so this component needs no
 * router and no context — it is mounted under both `/owner/*` and `/advisor/*`
 * and an advisor must not be sent to an owner-only path.
 */
function estimateHref(estimateId: number): string {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const base = path.startsWith('/advisor') ? '/advisor' : '/owner';
  return `${base}/estimates/${estimateId}`;
}

const ApprovalReviewQueue: React.FC = () => {
  const [queue, setQueue] = useState<ApprovalSubmission[]>([]);
  const [stalled, setStalled] = useState<StalledSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [cards, setCards] = useState<Record<number, CardState>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.get('/approval-review/queue');
      setQueue(Array.isArray(res?.data?.queue) ? res.data.queue : []);
      setStalled(res?.data?.stalled ?? null);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response
        ?.data;
      setLoadError(data?.error || data?.message || 'Could not load the review queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const cardOf = (id: number): CardState => cards[id] ?? BLANK;
  const patchCard = (id: number, patch: Partial<CardState>) =>
    setCards((prev) => ({ ...prev, [id]: { ...(prev[id] ?? BLANK), ...patch } }));

  const setDecision = (estimateId: number, changeId: number, accept: boolean) => {
    const current = cardOf(estimateId);
    patchCard(estimateId, {
      decisions: { ...current.decisions, [changeId]: accept },
      error: '',
    });
  };

  const setFieldNote = (estimateId: number, changeId: number, note: string) => {
    const current = cardOf(estimateId);
    patchCard(estimateId, { fieldNotes: { ...current.fieldNotes, [changeId]: note } });
  };

  const accepts = (state: CardState, changeId: number): boolean => state.decisions[changeId] !== false;

  /**
   * Bulk decision for one submission.
   *
   * Accepting everything was always the default (an absent decision means accept),
   * but nothing on the screen said so, and there was no way to reject a dozen
   * corrections without a dozen taps. Owner ask, 2026-07-30: "the approval route
   * should have an accept all".
   */
  const setAllDecisions = (submission: ApprovalSubmission, accept: boolean) =>
    patchCard(submission.estimate_id, {
      decisions: Object.fromEntries(submission.changes.map((c) => [c.id, accept])),
      error: '',
    });

  const submitAccept = async (submission: ApprovalSubmission) => {
    const id = submission.estimate_id;
    const state = cardOf(id);

    // Order follows the payload, so what the server records lines up with what
    // the reviewer read top to bottom.
    const decisions = submission.changes.map((change) => {
      const accept = accepts(state, change.id);
      const note = (state.fieldNotes[change.id] ?? '').trim();
      // The key is omitted rather than sent empty: the server stores this note on
      // the change request, and a blank string there is worse than no note.
      return note ? { id: change.id, accept, note } : { id: change.id, accept };
    });

    patchCard(id, { busy: true, error: '' });
    try {
      await api.post(`/approval-review/${id}/accept`, { decisions });
      message.success('Review recorded.');
      await load();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response
        ?.data;
      patchCard(id, {
        busy: false,
        error: data?.error || data?.message || 'Could not record that review.',
      });
    }
  };

  const submitReturn = async (submission: ApprovalSubmission) => {
    const id = submission.estimate_id;
    const state = cardOf(id);
    const note = state.returnNote.trim();

    if (!note) {
      // Not posted. The server refuses an unexplained bounce anyway, and a
      // customer who is sent back with no reason cannot do anything about it.
      patchCard(id, { returnOpen: true, error: 'Tell the customer what needs fixing.' });
      return;
    }

    const issues = submission.changes
      .map((change) => ({ id: change.id, note: (state.fieldNotes[change.id] ?? '').trim() }))
      .filter((issue) => issue.note.length > 0);

    patchCard(id, { busy: true, error: '' });
    try {
      await api.post(`/approval-review/${id}/return`, { return_note: note, issues });
      message.success('Sent back to the customer.');
      await load();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response
        ?.data;
      patchCard(id, {
        busy: false,
        error: data?.error || data?.message || 'Could not return that submission.',
      });
    }
  };

  const renderChange = (submission: ApprovalSubmission, change: ApprovalChange) => {
    const state = cardOf(submission.estimate_id);
    const accepted = accepts(state, change.id);

    return (
      <div
        key={change.id}
        data-testid={`change-${change.id}`}
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          padding: 10,
          background: accepted ? '#ffffff' : '#fff7f6',
        }}
      >
        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
          {labelOf(change)}
        </Text>

        {/* Stacks on a phone, sits side by side from 480px up. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              On file
            </Text>
            <Text delete={accepted} style={{ fontSize: 14, wordBreak: 'break-word' }}>
              {shownValue(change.current_value)}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              Customer says
            </Text>
            <Text strong style={{ fontSize: 14, wordBreak: 'break-word' }}>
              {shownValue(change.requested_value)}
            </Text>
          </div>
        </div>

        <Space wrap size={8}>
          <Button
            size="small"
            type={accepted ? 'primary' : 'default'}
            icon={<CheckOutlined />}
            data-testid={`accept-change-${change.id}`}
            aria-pressed={accepted}
            onClick={() => setDecision(submission.estimate_id, change.id, true)}
            style={{ minHeight: 44, paddingInline: 12 }}
          >
            Use theirs
          </Button>
          <Button
            size="small"
            danger={!accepted}
            type={accepted ? 'default' : 'primary'}
            icon={<CloseOutlined />}
            data-testid={`reject-change-${change.id}`}
            aria-pressed={!accepted}
            onClick={() => setDecision(submission.estimate_id, change.id, false)}
            style={{ minHeight: 44, paddingInline: 12 }}
          >
            Keep ours
          </Button>
        </Space>

        {state.returnOpen && (
          <TextArea
            data-testid={`field-note-${change.id}`}
            aria-label={`What is wrong with ${labelOf(change)}`}
            value={state.fieldNotes[change.id] ?? ''}
            onChange={(e) => setFieldNote(submission.estimate_id, change.id, e.target.value)}
            placeholder={`What is wrong with "${labelOf(change)}"? The customer will see this.`}
            autoSize={{ minRows: 1, maxRows: 3 }}
            // 16px stops mobile Safari zooming the viewport on focus.
            style={{ fontSize: 16, marginTop: 8 }}
          />
        )}
      </div>
    );
  };

  const renderSubmission = (submission: ApprovalSubmission) => {
    const id = submission.estimate_id;
    const state = cardOf(id);
    const bounced = Number(submission.return_count ?? 0) > 0;

    return (
      <Card
        key={id}
        data-testid={`submission-${id}`}
        size="small"
        styles={{ body: { padding: 12 } }}
        title={
          <Space wrap size={8}>
            <a href={estimateHref(id)}>{submission.estimate_number ?? `Estimate #${id}`}</a>
            {bounced && (
              <Tag color="orange" icon={<WarningOutlined />} data-testid={`bounced-${id}`}>
                {`Returned ${submission.return_count} ${
                  Number(submission.return_count) === 1 ? 'time' : 'times'
                }`}
              </Tag>
            )}
          </Space>
        }
        extra={
          submission.submitted_at ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {String(submission.submitted_at).slice(0, 16)}
            </Text>
          ) : null
        }
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {submission.changes.length === 0 ? (
            <Text type="secondary">
              They approved without changing anything. Accepting just settles it.
            </Text>
          ) : (
            <>
              {submission.changes.length > 1 && (
                <Space wrap size={8}>
                  <Button
                    size="small"
                    data-testid={`accept-all-${id}`}
                    onClick={() => setAllDecisions(submission, true)}
                    style={{ minHeight: 44, paddingInline: 12 }}
                  >
                    Use all theirs
                  </Button>
                  <Button
                    size="small"
                    data-testid={`reject-all-${id}`}
                    onClick={() => setAllDecisions(submission, false)}
                    style={{ minHeight: 44, paddingInline: 12 }}
                  >
                    Keep all ours
                  </Button>
                </Space>
              )}
              {submission.changes.map((change) => renderChange(submission, change))}
            </>
          )}

          {state.returnOpen && (
            <div>
              <Text style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                What needs fixing? (the customer will see this)
              </Text>
              <TextArea
                data-testid={`return-note-${id}`}
                aria-label="What needs fixing"
                value={state.returnNote}
                onChange={(e) => patchCard(id, { returnNote: e.target.value, error: '' })}
                placeholder="e.g. The street number does not match the suburb you gave us."
                autoSize={{ minRows: 2, maxRows: 5 }}
                style={{ fontSize: 16 }}
              />
            </div>
          )}

          {state.error && (
            <Alert type="error" showIcon message={state.error} data-testid={`error-${id}`} />
          )}

          <Space wrap size={8}>
            <Button
              type="primary"
              loading={state.busy}
              data-testid={`accept-submission-${id}`}
              onClick={() => submitAccept(submission)}
              style={{ minHeight: 44, paddingInline: 16 }}
            >
              {/* Names what the button will actually do. "Accept review" hid that
                  an untouched card accepts every value the customer sent. */}
              {submission.changes.length === 0
                ? 'Accept review'
                : submission.changes.every((c) => accepts(state, c.id))
                  ? `Accept all ${submission.changes.length} change${submission.changes.length === 1 ? '' : 's'}`
                  : `Accept review (${submission.changes.filter((c) => accepts(state, c.id)).length} of ${submission.changes.length})`}
            </Button>
            <Button
              danger
              loading={state.busy}
              icon={<ArrowLeftOutlined />}
              data-testid={`return-submission-${id}`}
              onClick={() => submitReturn(submission)}
              style={{ minHeight: 44, paddingInline: 16 }}
            >
              Send back to customer
            </Button>
          </Space>
        </Space>
      </Card>
    );
  };

  return (
    <div
      style={{ padding: '16px 12px', maxWidth: 900, margin: '0 auto', boxSizing: 'border-box' }}
      data-testid="approval-review-queue"
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>
            Approval review
          </Title>
          <Text type="secondary">
            Corrections customers sent with their approvals. Nothing here has been written to their
            record yet.
          </Text>
        </div>

        {/*
          Spec §10.1 — the stalled-approvals signal. The shop must SEE approvals
          stalling rather than infer it from a quiet week, because the requirement
          gate is on by default.
        */}
        {stalled && stalled.count > 0 && (
          <Alert
            type="warning"
            showIcon
            data-testid="stalled-approvals"
            message={`Waiting on customers: ${stalled.count} sent ${
              stalled.count === 1 ? 'estimate' : 'estimates'
            } cannot be approved yet`}
            description={
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                {stalled.estimates.map((est) => (
                  <Text key={est.estimate_id} style={{ fontSize: 13 }}>
                    <a href={estimateHref(est.estimate_id)}>
                      {est.estimate_number ?? `Estimate #${est.estimate_id}`}
                    </a>
                    {est.missing.length > 0 ? ` — still needs ${est.missing.join(', ')}` : ''}
                  </Text>
                ))}
              </Space>
            }
          />
        )}

        {loadError && (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={() => void load()}>
                Retry
              </Button>
            }
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : queue.length === 0 ? (
          <Card size="small">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nothing waiting for review right now."
            />
          </Card>
        ) : (
          queue.map((submission) => renderSubmission(submission))
        )}
      </Space>
    </div>
  );
};

export default ApprovalReviewQueue;
